import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useCurrentPermissions } from "../../contexts/PermissionsProvider";
import AssignmentPickerDialog from "../../components/Directory/AssignmentPickerDialog";
import {
  ActionRow,
  EmptyState,
  InlineFilter,
  PageHeader,
  ScopeBadge,
  ScopeSelector,
  SkeletonTable,
} from "../../components/Directory/primitives";
import { dirTokens } from "../../components/Directory/tokens";
import {
  createRole,
  deleteRole,
  getAccounts,
  getRoles,
  updateRole,
} from "./api";

/**
 * Tooltip copy for the "Assign to…" affordances. Kept as a shared
 * constant so the kebab item, the hover-reveal icon, and the detail
 * page's primary CTA all speak with one voice — and so future edits
 * only touch one line.
 */
const ASSIGN_TOOLTIP =
  "Grant this role to a user or group at the chosen scope.";

/**
 * DirectoryRoles — editable Roles list.
 *
 * SYSTEM: can create predefined roles (POST /api/roles with
 *   predefined=true).
 * ACCOUNT_ADMIN: can create custom roles under their own account
 *   (coord infers accountId from the caller when predefined=false).
 *
 * Row click routes to /directory/roles/{id} — the detail page shows
 * entitlements + assigned-to lists and hosts the AssignmentPicker.
 *
 * Predefined roles (r.predefined === true or SYSTEM-scoped with null
 * accountId + no createdBy) render with rename/delete disabled and a
 * "system-managed" tooltip. Non-SYSTEM callers only see rename/delete
 * for roles their account owns.
 */

function isPredefined(r) {
  return (
    r.predefined === true ||
    (r.predefined === undefined && (r.accountId === null || r.accountId === undefined))
  );
}

const COLUMN_SPEC = [
  { label: "Name" },
  { label: "Scope", width: 200 },
  { label: "Type", width: 120 },
  { label: "Assignments", align: "right", width: 120 },
  { label: "Description" },
  { label: "ID", mono: true },
  { label: "", align: "right", width: 48 },
];

function assignmentCount(r) {
  const users = Array.isArray(r.assignedUsers)
    ? r.assignedUsers.length
    : typeof r.assignedUserCount === "number"
      ? r.assignedUserCount
      : null;
  const groups = Array.isArray(r.assignedGroups)
    ? r.assignedGroups.length
    : typeof r.assignedGroupCount === "number"
      ? r.assignedGroupCount
      : null;
  if (users === null && groups === null) return null;
  return (users ?? 0) + (groups ?? 0);
}

export default function DirectoryRoles() {
  const { permissions } = useCurrentPermissions();
  const isSystem =
    (permissions || []).includes("SYSTEM") ||
    (permissions || []).includes("SYSTEM_DIRECTORY_ADMIN");
  const navigate = useNavigate();

  const [roles, setRoles] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [toast, setToast] = useState(null);

  // SYSTEM-only scope selector state, persisted in URL:
  //   ?scope=all|predefined|account  &  ?accountId=<uuid>
  const [searchParams, setSearchParams] = useSearchParams();
  const rawScope = searchParams.get("scope");
  const scope =
    rawScope === "predefined" || rawScope === "account" ? rawScope : "all";
  const accountId = searchParams.get("accountId") || null;

  // Accounts list is only needed for the SYSTEM picker.
  const [accounts, setAccounts] = useState(null);
  useEffect(() => {
    if (!isSystem) return;
    let cancelled = false;
    getAccounts()
      .then((data) => {
        if (!cancelled) setAccounts(data || []);
      })
      .catch(() => {
        if (!cancelled) setAccounts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isSystem]);

  const [newOpen, setNewOpen] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [deleting, setDeleting] = useState(null);
  // Which role's "Assign to…" picker is open. null when closed.
  const [assigning, setAssigning] = useState(null);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  // Scope → coord query payload:
  //   "predefined" → accountId omitted + includePredefined=true (only
  //                  predefined rows have null accountId, so coord
  //                  returns just the platform bucket when we also
  //                  ask includePredefined=only via absence of accountId
  //                  and a truthy predefined-only flag — expressed here
  //                  as accountId="none" sentinel below).
  //   "account"    → accountId=<uuid> + includePredefined=false (only
  //                  the account's custom roles).
  //   "all"        → no narrows.
  //
  // Because coord's current filter contract lets accountId=null mean
  // "no narrow" (not "only predefined"), we lean on `includePredefined`
  // to disambiguate: predefined-only is scope=null + predefined=true
  // with an accountIdEquals=null server-side hint expressed as
  // `predefinedOnly=true`.
  const scopePayload = useMemo(() => {
    if (scope === "predefined") return { predefinedOnly: true };
    if (scope === "account" && accountId)
      return { accountId, includePredefined: false };
    return {};
  }, [scope, accountId]);

  const load = useCallback(() => {
    setErr(null);
    setRoles(null);
    getRoles(scopePayload)
      .then(setRoles)
      .catch((e) =>
        setErr(
          e.status === 403
            ? "You don't have permission to view roles on this account."
            : `Failed to load roles: ${e.message}`
        )
      );
  }, [scopePayload]);

  useEffect(() => {
    load();
  }, [load]);

  const handleScopeChange = ({ scope: nextScope, accountId: nextAccountId }) => {
    const next = new URLSearchParams(searchParams);
    if (nextScope && nextScope !== "all") next.set("scope", nextScope);
    else next.delete("scope");
    if (nextAccountId) next.set("accountId", nextAccountId);
    else next.delete("accountId");
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(
    () =>
      roles?.filter(
        (r) =>
          !q ||
          (r.name ?? "").toLowerCase().includes(q.toLowerCase()) ||
          (r.description ?? "").toLowerCase().includes(q.toLowerCase()) ||
          (r.id ?? "").toLowerCase().includes(q.toLowerCase())
      ) ?? [],
    [roles, q]
  );

  const openMenu = (e, row) => {
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const menuPredefined = menuRow ? isPredefined(menuRow) : false;

  const newRoleButton = (
    <Tooltip
      title="Create a custom role. Pick entitlements after creation."
      arrow
      enterDelay={400}
    >
      <span>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setNewOpen(true)}
          sx={{ textTransform: "none" }}
          aria-label="New role"
        >
          New role
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      <PageHeader
        title="Roles"
        titleInfo="A role bundles entitlements (verb.noun permissions) so you grant one label instead of a list."
        subtitle="Roles bundle entitlements. Assign one to a user or a group from the role's detail page."
        action={newRoleButton}
      />

      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      <ScopeSelector
        variant="roles"
        value={{ scope, accountId }}
        onChange={handleScopeChange}
        accounts={accounts}
        hidden={!isSystem}
        sx={{ mb: 2 }}
      />

      {!roles && !err && <SkeletonTable columns={COLUMN_SPEC} rows={6} />}

      {roles && (
        <>
          <InlineFilter
            value={q}
            onChange={setQ}
            placeholder="Filter by name, description, or id"
            count={{
              shown: filtered.length,
              total: roles.length,
              noun: "role",
            }}
          />

          {filtered.length === 0 ? (
            <EmptyState
              icon={<ShieldOutlinedIcon />}
              title={
                q || scope !== "all" ? "No roles match" : "No roles yet"
              }
              description={
                q || scope !== "all"
                  ? "Try clearing the filter or widening the scope."
                  : "Create a role to bundle entitlements and assign it to users or groups."
              }
              variant={q || scope !== "all" ? "no-match" : "empty"}
              primaryAction={q || scope !== "all" ? undefined : newRoleButton}
            />
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: dirTokens.radius }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {COLUMN_SPEC.map((c, i) => (
                      <TableCell
                        key={c.label || `col-${i}`}
                        align={c.align || "left"}
                        sx={c.mono ? { fontFamily: "monospace" } : undefined}
                      >
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((r) => {
                    const predef = isPredefined(r);
                    const asgn = assignmentCount(r);
                    return (
                      <ActionRow
                        key={r.id}
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate(`/directory/roles/${r.id}`)}
                        aria-label={`Open ${r.name || "role"} — view entitlements and who holds this role`}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={500}>
                              {r.name || "—"}
                            </Typography>
                            {predef && (
                              <Tooltip title="System-managed — cannot be renamed or deleted">
                                <LockOutlinedIcon
                                  fontSize="small"
                                  sx={{ color: "text.disabled" }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {r.accountId ? (
                            <ScopeBadge
                              scope="account"
                              name={r.accountName || r.accountId}
                            />
                          ) : (
                            <ScopeBadge scope="system" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={predef ? "Predefined" : "Custom"}
                            size="small"
                            variant="outlined"
                            color={predef ? "secondary" : "default"}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <span
                            style={{
                              fontVariantNumeric: "tabular-nums",
                              fontSize: 13,
                            }}
                          >
                            {asgn ?? "—"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={r.description || ""} arrow>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                maxWidth: 360,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {r.description || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.75rem",
                            color: "text.secondary",
                          }}
                        >
                          {r.id}
                        </TableCell>
                        <TableCell align="right" sx={{ width: 96 }}>
                          <Box
                            className="row-actions"
                            sx={{ display: "inline-flex", gap: 0.5 }}
                          >
                            <Tooltip title={ASSIGN_TOOLTIP}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAssigning(r);
                                }}
                                aria-label={`Assign to… (${r.name || "role"})`}
                              >
                                <PersonAddOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="More actions">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openMenu(e, r);
                                }}
                                aria-label="Role actions"
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </ActionRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
<Tooltip title={ASSIGN_TOOLTIP} arrow enterDelay={400} placement="left">
          <MenuItem
            onClick={() => {
              const row = menuRow;
              closeMenu();
              if (row) setAssigning(row);
            }}
          >
            Assign to…
          </MenuItem>
        </Tooltip>
        <Tooltip
          title="Open the role's detail page — entitlements and everyone who holds it."
          arrow
          enterDelay={400}
          placement="left"
        >
          <MenuItem
            onClick={() => {
              if (menuRow) navigate(`/directory/roles/${menuRow.id}`);
              closeMenu();
            }}
          >
            Open detail
          </MenuItem>
        </Tooltip>
        <Tooltip
          title={
            menuPredefined
              ? "System-managed — protected from renaming."
              : "Change the role's name or description. Entitlements and assignments stay put."
          }
          arrow
          enterDelay={400}
          placement="left"
        >
          <span>
            <MenuItem
              onClick={() => {
                setRenaming(menuRow);
                closeMenu();
              }}
              disabled={menuPredefined}
            >
              {menuPredefined ? "Rename (system-managed)" : "Rename…"}
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          title={
            menuPredefined
              ? "System-managed — protected from deletion."
              : "Delete the role. Users and groups holding it lose the entitlements immediately."
          }
          arrow
          enterDelay={400}
          placement="left"
        >
          <span>
            <MenuItem
              onClick={() => {
                setDeleting(menuRow);
                closeMenu();
              }}
              disabled={menuPredefined}
              sx={{ color: menuPredefined ? "text.disabled" : "error.main" }}
            >
              {menuPredefined ? "Delete (system-managed)" : "Delete…"}
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>

      <RoleCreateDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        canPredefine={isSystem}
        onCreated={(r) => {
          setNewOpen(false);
          setToast({ message: `Role "${r.name}" created.` });
          load();
        }}
      />

      {renaming && (
        <RoleRenameDialog
          role={renaming}
          onClose={() => setRenaming(null)}
          onSaved={() => {
            setRenaming(null);
            setToast({ message: "Role updated." });
            load();
          }}
        />
      )}

      {deleting && (
        <RoleDeleteDialog
          role={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            setToast({ message: "Role deleted." });
            load();
          }}
        />
      )}

      <AssignmentPickerDialog
        open={!!assigning}
        mode="assign_role"
        roleId={assigning?.id}
        roleName={assigning?.name}
        scopeAccountId={assigning?.accountId || null}
        onClose={() => setAssigning(null)}
        onSaved={({ count }) => {
          setAssigning(null);
          setToast({ message: `Assigned to ${count} subject(s).` });
          load();
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            severity="success"
            onClose={() => setToast(null)}
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </motion.div>
  );
}

// ---------------- subcomponents ----------------

export function RoleCreateDialog({ open, canPredefine, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // SYSTEM callers get a predefined toggle; ACCOUNT_ADMIN callers
  // always mint custom roles.
  const [predefined, setPredefined] = useState(!!canPredefine);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setDescription("");
    setPredefined(!!canPredefine);
    setError(null);
    setBusy(false);
  }, [open, canPredefine]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await createRole({
        name,
        description,
        predefined: canPredefine && predefined,
      });
      onCreated && onCreated(r || { name });
    } catch (e) {
      setError(e.message || "Create failed. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New role</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            required
            fullWidth
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {canPredefine && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={predefined}
                  onChange={(e) => setPredefined(e.target.checked)}
                />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    Predefined (platform-wide)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    SYSTEM only. Ships to every account and can&apos;t be
                    renamed or deleted from the console.
                  </Typography>
                </Box>
              </label>
            </Box>
          )}
          {error && (
            <Typography sx={{ color: "error.main" }}>{error}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || !name.trim()}
        >
          {busy ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RoleRenameDialog({ role, onClose, onSaved }) {
  const [name, setName] = useState(role?.name || "");
  const [description, setDescription] = useState(role?.description || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateRole({ rid: role.id, name, description });
      onSaved && onSaved();
    } catch (e) {
      setError(e.message || "Update failed. Try again in a moment.");
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Rename role</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            required
            fullWidth
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {error && (
            <Typography sx={{ color: "error.main" }}>{error}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy || !name.trim()}
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RoleDeleteDialog({ role, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteRole({ rid: role.id });
      onDeleted && onDeleted();
    } catch (e) {
      setError(e.message || "Delete failed. Try again in a moment.");
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete {role.name}?</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2">
          Users and groups holding this role lose it immediately. The
          entitlements themselves remain — you can rebuild the bundle
          later if needed.
        </Typography>
        {error && (
          <Typography sx={{ color: "error.main", mt: 2 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={submit}
          disabled={busy}
        >
          {busy ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
